/**
 * The year, as the land wears it — one number, shared by every surface that
 * has to agree about what month it is.
 *
 * `world/weather.ts` has computed `seasonalDryness(t)` since the natural-fire
 * work and its docstring has been an invitation ever since: *"a renderer may
 * one day straw-tint late-summer grass with the same number, and then the land
 * will look exactly as flammable as it is."* Until now it had **zero consumers
 * in `src/`** — the simulation burned by it and nothing drew it.
 *
 * ## Why this is a uniform and not a colour
 *
 * Ground colour is baked. `groundSurface` runs in a chunk worker, takes no
 * time argument, and its result is a vertex attribute on a cached mesh — so
 * curing the land on the CPU means rebuilding the ~1,200-chunk streamed ring
 * every time the season moves, which is continuously. Plant colour is baked
 * the same way, per instance. The horizon's is a 320² Float32 attribute whose
 * full refill measures ~333 ms.
 *
 * So the season cannot travel through any of the paths that already carry
 * history. It is a fragment-shader multiply on a shared `{value}` object —
 * the same shape as `chunkView.ts`'s `reliefStrength` and the vegetation
 * kit's `wind` — assigned into three materials' uniforms by reference, so
 * moving it costs one float and rebuilds nothing at all.
 *
 * ## And why the mask is not simply "the grass"
 *
 * The obvious rule is to cure by the ground's grass weight, `vMat.x`. Measured
 * over 63,018 land samples of the real island, that weight is **mean 0.130,
 * with 56.2% of land under 0.1** — it is a GRAIN weight, deciding where blade
 * stipple is drawn, not a "how much living cover" weight. Keying the cure on
 * it alone moves under a quarter of the island. Adding a fraction of the
 * organic-floor family gives the mask real coverage — grassland 0.138 → 0.390,
 * oak 0.155 → 0.413, redwood 0.266 → 0.469, chaparral 0.068 → 0.245 — while
 * conifer (0.055) and alpine (0.046) stay effectively exempt, because their
 * rock weight already crowds both families out. Sand and seabed are exempt by
 * construction: `groundSurface` forces the grass weight to 0 below the wash
 * and fades it across every beach.
 *
 * The alternative of reading the tint's own hue does not work and was measured
 * too: greenness never exceeds 0.045 anywhere on the island, so there is no
 * per-pixel green signal in the baked colour to shift.
 */

/** How cured the land is, 0..1. Assigned by reference; never reassign it. */
export const dryness = { value: 0 };

/** How much of the organic-floor family joins the grass in the mask. */
export const ORGANIC_SHARE = 0.35;

/**
 * The GLSL every ground material shares, appended to its `#include <common>`.
 *
 * One function, three call sites — the shader analogue of the rule that made
 * `envTint`/`scarTint` a single function, and the only thing standing between
 * the near ground and the far ground agreeing about the month.
 *
 * The straw is the palette's OWN dry tint, `[126,116,76]` from `landColour`'s
 * low-rainfall branch, pre-multiplied by `GROUND_EXPOSURE = 0.56` because the
 * baked vertex colour lives in that scaled space. Writing the unexposed value
 * here would cure the land to a colour brighter than any the palette can
 * produce.
 */
export const DRY_GLSL = `
uniform float uDry;
// the palette's own straw, pre-exposed by GROUND_EXPOSURE (0.56)
const vec3 DRY_STRAW = vec3(0.2765, 0.2546, 0.1667);
vec3 seasonTint(vec3 c, float cover, float dry) {
  float cure = clamp(cover, 0.0, 1.0) * clamp(dry, 0.0, 1.0);
  // toward straw rather than a multiply: the ground's own palette already
  // reaches this colour on genuinely dry ground, so a cured meadow and a
  // permanently arid slope land on the same hue instead of two different ones
  return mix(c, DRY_STRAW, cure * 0.62);
}
`;
